import { message, Modal } from "ant-design-vue";
import { store } from "@/store/index";
import { ContentTypeEnum } from "@/utils/enum";
import { h } from "vue";
import type { AxiosRequestConfig, Method } from "axios";
import axios from "axios";
import dayjs from "dayjs";
import qs from "qs";
import { Loading } from "./loading";
import { doLogin } from "./oidcServer";
import { storage } from "./storage";

interface IOptions {
  url: string;
  method?: Method;
  data?: any;
  params?: any; // 接口参数
  options?: {
    isShowError?: boolean; // 是否弹出提示框
    isArrayObject?: boolean;
    baseURL?: string;
    timeout?: number;
    noTransform?: boolean;
    token?: string;
    tenantId?: string;
    tenantName?: string;
    isReturnResponse?: boolean; // 返回完整的response
    relationRequestForKey?: string; // 关联请求的key值（回传给请求头参数relation-request-for-key）
  };
}

export default function $http<T>({ url, method = "GET", data, params, options = { isArrayObject: false, noTransform: false } }: IOptions) {
  return new Promise<T>((resolve, reject) => {
    const instance = axios.create({
      baseURL: "/sso",
      timeout: 120000,
    });
    instance.interceptors.request.use(
      (config: AxiosRequestConfig) => {
        options.isShowError = options.isShowError ?? true;
        const DefaultTenantId = "00000000-0000-0000-0000-000000000000";
        const token = options.token ?? storage.get("storeIndex-token");
        const TenantId = options.tenantId ?? DefaultTenantId;
        const ExamDetail = store.state.userOperationExamMap[TenantId]?.examDetail || {};
        const TenantName = encodeURIComponent(options.tenantName || ExamDetail.examName || "");
        const RelationRequestForKey = options.relationRequestForKey || "";

        config.headers["Authorization"] = `bearer ${token}`;
        config.headers["relation-request-for-key"] = RelationRequestForKey;
        config.headers["TenantId"] = TenantId;
        config.headers["TenantName"] = TenantName;
        config.headers["X-Client-Platform"] = "pc";

        if (options.noTransform) {
          return config;
        }
        if (config.method == "delete" && !url.includes("batch")) {
          config.headers["Content-Type"] = ContentTypeEnum.FORM_URLENCODED;
          config.data = qs.stringify(data, { arrayFormat: "repeat" });
          return config;
        }
        if (config.method != "delete" && !url.includes("batch") && options.isArrayObject == false) {
          config.data = qs.stringify(data, {
            arrayFormat: "repeat",
          });
          return config;
        }
        if (options.isArrayObject == true) {
          config.headers["Content-Type"] = ContentTypeEnum.FORM_URLENCODED;
          config.data = qs.stringify(data);
          return config;
        }

        config.data = qs.stringify(data);
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    instance.interceptors.response.use(
      (response) => {
        const res = options.isReturnResponse ? response : response.data;
        const localTime = dayjs(new Date());
        const serverTime = dayjs(response.headers.date);
        // 计算本地时候与服务器时间是否差异过大（以10分钟为单位）
        const isDiffInMinutes = Math.abs(serverTime.diff(localTime, "minute")) > 10;
        store.commit("SET_isTimeDifferenceExcessive", isDiffInMinutes);

        if (isDiffInMinutes && !store.state.isPopupDisplayed) {
          Modal.destroyAll();
          Modal.warning({
            title: "操作提示",
            content: h("div", {}, [h("p", "当前电脑时间与服务器时间不一致，请先关闭浏览器，校准本地电脑时间后重新打开浏览器登录!")]),
            // okText:"确认",
            keyboard: false,
            centered: true,
            maskClosable: false,
            onOk() {
              store.commit("SET_isPopupDisplayed", false);
              window.opener = null;
              window.open(window.location.href, "_self");
              window.close();
            },
          });
          store.commit("SET_isPopupDisplayed", true);
        }

        if (response.status == 200 || response.status == 204) {
          store.commit("SET_DATA", { name: "upgradeData", data: null });
          return res;
        } else {
          return Promise.reject(new Error(res.message || res.errors || "Error"));
        }
      },
      async (err: any) => {
        if (err && err.response) {
          switch (err.response.status) {
            case 400:
              let msg = "请求错误";
              const errors = err.response.data.errors;
              if (typeof errors === "string") {
                err.message = errors;
              } else {
                for (const key in errors) {
                  if (Object.prototype.hasOwnProperty.call(errors, key)) {
                    const val = errors[key];
                    msg = val.join(",");
                  }
                }
                err.message = msg;
              }
              break;

            case 401:
              message.destroy();
              message.error("当前用户未授权，请登录");
              doLogin();
              break;

            case 403:
              err.message = "权限不足,拒绝访问";
              break;

            case 404:
              err.message = err.response.data.errors ? err.response.data.errors : "请求资源不存在";
              console.log(`请求资源不存在:${err.response.config.url}`);
              break;

            case 405:
              err.message = "请求方法不允许";
              break;

            case 408:
              err.message = "请求超时";
              break;

            case 413:
              err.message = "请求实体过大";
              break;

            case 500:
              err.message = "当前操作出错，请稍侯再试";
              break;

            case 501:
              err.message = "服务未实现";
              break;

            case 502:
              err.message = "网关错误";
              break;

            case 503:
              if (err.response.data.code == 288) {
                options.isShowError = false;
                store.commit("SET_DATA", { name: "upgradeData", data: err.response.data });
              }
              err.message = "服务不可用";
              break;

            case 504:
              err.message = "网关超时";
              break;

            case 505:
              err.message = "HTTP版本不受支持";
              break;

            case 568:
              const arrErorrs = [] as any;
              const responseErrors: any = err.response.data.errors;
              Object.keys(responseErrors).forEach((key: any) => {
                arrErorrs.push(responseErrors[key]);
              });
              const strErorrs = arrErorrs.join("");
              err.message = strErorrs;
              break;
            default:
              err.message = { ...err.response.data.errors };
          }
        }

        // console.warn(err.response);
        if (err.code === "ECONNABORTED" && err.message.indexOf("timeout") !== -1) {
          err.message = "请求超时,请重试";
        }

        return Promise.reject(err); // 返回接口返回的错误信息
      },
    );
    // 请求处理
    instance(url, { method, params, data })
      .then((res: any) => {
        resolve(res);
      })
      .catch((error: any) => {
        Loading.hide();
        if (options.isShowError) message.error(error.message);
        reject(error);
      });
  });
}
